import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiRequest from 'helpers/api';

const initialState = [];

/**
 * API errors are `{ error: { message, log } }` from `apiRequest` rejection (`response.data`).
 * The backend puts the generic HTTP reason (e.g. "Bad request") in `message` and the
 * specific, user-facing reason (e.g. duplicate tag name, delete blocked) in `log`, so
 * prefer `log` over `message` — matching `getFetchBaseQueryErrorMessage` in helpers/api.
 */
export function apiErrorMessage(error, fallback) {
  if (error == null) return fallback;
  if (typeof error === 'string') return error.trim() || fallback;
  const nested = error.error;
  const log =
    (typeof nested?.log === 'string' && nested.log.trim() && nested.log) ||
    (typeof error.log === 'string' && error.log.trim() && error.log);
  if (log) return log;
  const msg =
    (typeof nested?.message === 'string' && nested.message.trim() && nested.message) ||
    (typeof error.message === 'string' && error.message.trim() && error.message);
  if (msg) return msg;
  return fallback;
}

// MySQL returns tinyint/count columns as strings — normalise to JS types.
const normalizeTag = tag => ({
  ...tag,
  id: Number(tag.id),
  is_system: Boolean(Number(tag.is_system)),
  usage_count: Number(tag.usage_count ?? 0),
  archived_usage_count: Number(tag.archived_usage_count ?? 0),
  assignment_count: Number(tag.assignment_count ?? 0),
});

export const getTags = createAsyncThunk('getTags', async () => {
  const res = await apiRequest(`tags`, {}, 'get');
  return (res.result || []).map(normalizeTag);
});

export const createTag = createAsyncThunk('createTag', async (data, { rejectWithValue }) => {
  try {
    const res = await apiRequest('tags', data, 'post');
    return normalizeTag(res.result);
  } catch (error) {
    return rejectWithValue({ message: apiErrorMessage(error, 'Failed to create tag') });
  }
});

export const updateTag = createAsyncThunk(
  'updateTag',
  async ({ id, ...data }, { rejectWithValue }) => {
    try {
      const res = await apiRequest(`tags/${id}`, data, 'put');
      return normalizeTag(res.result);
    } catch (error) {
      return rejectWithValue({ message: apiErrorMessage(error, 'Failed to update tag') });
    }
  },
);

export const deleteTag = createAsyncThunk('deleteTag', async (id, { rejectWithValue }) => {
  try {
    await apiRequest(`tags/${id}`, {}, 'delete');
    return id;
  } catch (error) {
    return rejectWithValue({ message: apiErrorMessage(error, 'Failed to delete tag') });
  }
});

export const bulkDeleteTags = createAsyncThunk(
  'bulkDeleteTags',
  async (ids, { rejectWithValue }) => {
    try {
      const res = await apiRequest('tags/bulk/delete', { ids }, 'post');
      // result: { deleted, skipped, deleted_ids }
      return { deletedIds: (res.result?.deleted_ids || []).map(Number), result: res.result };
    } catch (error) {
      return rejectWithValue({ message: apiErrorMessage(error, 'Failed to delete tags') });
    }
  },
);

export const mergeTags = createAsyncThunk(
  'mergeTags',
  async ({ sourceIds, targetId }, { rejectWithValue }) => {
    try {
      const res = await apiRequest(
        'tags/bulk/merge',
        { source_ids: sourceIds, target_id: targetId },
        'post',
      );
      return { sourceIds, updatedTarget: normalizeTag(res.result) };
    } catch (error) {
      return rejectWithValue({ message: apiErrorMessage(error, 'Failed to merge tags') });
    }
  },
);

const tagsSlice = createSlice({
  name: 'tags',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getTags.fulfilled, (_, { payload }) => {
        return payload;
      })
      .addCase(createTag.fulfilled, (state, { payload }) => {
        return [...state, payload];
      })
      .addCase(updateTag.fulfilled, (state, { payload }) => {
        return state.map(tag => {
          if (Number(tag.id) !== Number(payload.id)) return tag;
          return { ...tag, ...payload, created_at: tag.created_at };
        });
      })
      .addCase(deleteTag.fulfilled, (state, { payload: id }) => {
        return state.filter(tag => Number(tag.id) !== Number(id));
      })
      .addCase(bulkDeleteTags.fulfilled, (state, { payload: { deletedIds } }) => {
        // Only remove tags that the backend actually deleted (skipped ones stay)
        const idSet = new Set(deletedIds);
        return state.filter(tag => !idSet.has(Number(tag.id)));
      })
      .addCase(mergeTags.fulfilled, (state, { payload: { sourceIds, updatedTarget } }) => {
        const sourceSet = new Set(sourceIds.map(Number));
        const targetId = Number(updatedTarget.id);
        // Remove source tags but never remove the target (targetId may be in sourceIds).
        // Then replace the target entry with updatedTarget; if target wasn't in state, append it.
        const withoutSources = state.filter(
          tag => !sourceSet.has(Number(tag.id)) || Number(tag.id) === targetId,
        );
        const withUpdatedTarget = withoutSources.map(tag =>
          Number(tag.id) === targetId ? updatedTarget : tag,
        );
        const hasTarget = withUpdatedTarget.some(tag => Number(tag.id) === targetId);
        return hasTarget ? withUpdatedTarget : [...withUpdatedTarget, updatedTarget];
      });
  },
});

export const selectTags = state => state.tags;

export default tagsSlice.reducer;
